// Shared lesson-progress helpers for the Leader's Dashboard.
//
// The syllabus is SERVER-DEFINED. `GET courses/leader/context` returns
// `lessons: [{ ordinal, label, part }]` for whichever curriculum the leader is
// on — the old follow-up course is 8 lessons split 4/4 across its two booklets,
// the new Next Steps journey is 10 split 5/5.
//
// Nothing here may assume either shape. The previous version hardcoded
// `LESSONS_PER_PHASE = 5` / `TOTAL_LESSONS = 10`, which on the 8-lesson
// curriculum made "journey complete" unreachable and rendered a 4-lesson Part 1
// as "Part 1 · 2 of 5".

/** `entity[key]` as an array, tolerating a null entity or a missing field. */
function listOf(entity, key) {
  return (entity && entity[key]) || [];
}

/**
 * Booklet name for the UI.
 *
 * Centralised because C9 of `docs/plans/2026-08-06-ns-leader-dashboard-reconciliation.md`
 * resolved that UI copy should read "First Steps / Next Steps", not "Part 1 /
 * Part 2" — and that is still not what ships. It cannot be a straight rename
 * here: `leader/context` sends `part: 1 | 2` and no booklet NAME, and the two
 * booklets of the OLD follow-up curriculum are not called First Steps and Next
 * Steps. Renaming blind would mislabel every legacy roster.
 *
 * The fix belongs on the wire — a `partLabel` (or `parts: [{ part, label }]`) on
 * the context payload — after which this function reads it and the drift closes
 * in one place. Until then every part label in the feature comes from here.
 */
export function partLabel(part) {
  return `Part ${part}`;
}

/** Group the context's lessons into their two booklets, preserving order. */
export function partsFrom(lessons) {
  const rows = lessons || [];
  return [1, 2]
    .map(part => ({ part, lessons: rows.filter(lesson => lesson.part === part) }))
    .filter(group => group.lessons.length > 0);
}

/** Every ordinal a leader can confirm, in course order. */
export function ordinalsFrom(lessons) {
  return (lessons || []).map(lesson => lesson.ordinal);
}

/**
 * Every lesson this member has actually FINISHED, by either curriculum.
 *
 * `doneLessons` is what a leader confirmed here; `legacyLessons` is what was
 * completed under the previous follow-up. Both are completions — `lessonState`
 * has always ranked legacy above awaiting for exactly that reason, and the chip
 * grid has always drawn it filled.
 *
 * Every derived read goes through this. They used to read `doneLessons` alone,
 * so a member who finished the whole old curriculum sat on the roster reading
 * "Not started", with empty progress bars and "0 of 10 confirmed", directly
 * beside eight filled chips. The chips were right and everything else was wrong.
 */
export function creditedLessons(entity) {
  const credited = new Set([...listOf(entity, 'doneLessons'), ...listOf(entity, 'legacyLessons')]);
  return [...credited].sort((a, b) => a - b);
}

/** How much of this member's credited progress was carried over, not confirmed. */
export function legacyCount(entity) {
  const done = listOf(entity, 'doneLessons');
  return listOf(entity, 'legacyLessons').filter(ordinal => !done.includes(ordinal)).length;
}

export function isJourneyComplete(entity, lessonCount) {
  if (entity && entity.completedAt) return true;
  // Without a count there is no journey to have finished. Guessing one is how
  // completion became unreachable on the 8-lesson syllabus.
  if (!lessonCount) return false;
  return creditedLessons(entity).length >= lessonCount;
}

/**
 * { filled, total } for one lesson, or null when that lesson has no blanks.
 * Counts only — the API never sends what the member actually wrote.
 */
export function blanksFor(entity, ordinal) {
  return listOf(entity, 'blanksByLesson').find(row => row.ordinal === ordinal) || null;
}

/**
 * Has the member written anything in this lesson? At least one blank filled,
 * as counted server-side. This is what makes a lesson "in progress" before the
 * member reaches the end — until they tap Complete there is no LessonProgress
 * row, so without this a member halfway through a lesson looked untouched.
 */
export function hasStarted(entity, ordinal) {
  const blanks = blanksFor(entity, ordinal);
  return Boolean(blanks && blanks.filled > 0);
}

/**
 * Which of the five states one lesson is in for this member — the chip on the
 * roster card and the tick in the detail sheet both render off this.
 *
 * Order matters: a lesson completed under BOTH curricula reads as confirmed,
 * with the legacy origin shown in the detail sheet rather than the chip. Legacy
 * outranks awaiting — an old completion is a completion, not something waiting
 * on this leader. Awaiting outranks started: reaching the end supersedes
 * having written in it (decision 2026-09-02 — the blanks tally never gates the
 * prompt, it only distinguishes "started" from "untouched").
 */
export function lessonState(entity, ordinal) {
  if (listOf(entity, 'doneLessons').includes(ordinal)) return 'confirmed';
  if (listOf(entity, 'legacyLessons').includes(ordinal)) return 'legacy';
  if (listOf(entity, 'awaitingLessons').includes(ordinal)) return 'awaiting';
  if (hasStarted(entity, ordinal)) return 'started';
  return 'empty';
}

/**
 * Lessons with uncredited work in them, in course order: reached the end, or
 * at least one blank filled. The card's status line and the CG rollup both
 * need "is this member doing anything" without caring which of the two it is.
 */
export function activeLessons(entity, lessons) {
  return ordinalsFrom(lessons).filter(ordinal => {
    const state = lessonState(entity, ordinal);
    return state === 'awaiting' || state === 'started';
  });
}

/** Is this lesson finished by either curriculum? */
export function isCredited(entity, ordinal) {
  const state = lessonState(entity, ordinal);
  return state === 'confirmed' || state === 'legacy';
}

/**
 * Lessons the member has finished and no leader has confirmed — the prompt list.
 * Drives "they've completed this, confirm it?".
 *
 * A lesson counts as finished when the member reached the end and tapped
 * Complete — `awaitingLessons`, which is a LessonProgress row. That row is only
 * written by the Complete button, and CoursesFooter only shows it on the last
 * page (`currentPageNumber >= totalPages`), so it genuinely means "got to the
 * end", not "opened the lesson".
 *
 * The blanks tally (`blanksByLesson`) is NOT consulted. Decision 2026-09-02: it
 * used to be a second condition, and it held a genuinely finished lesson at
 * "in progress" whenever one answer never reached the server — the phone kept
 * showing the blank filled from its local cache while the API counted it empty,
 * and the leader had no way to tell why the prompt never came. Whether the
 * written work is done is for the leader to judge in the sit-down, not for a
 * row count to gate.
 *
 * NOTE what this does NOT do: nothing here writes. The member reaching the end
 * records their own progress and no more; the Next Steps completion and the
 * Follow Up attendance are written only when the leader actually marks it.
 */
export function readyToConfirm(entity, lessons) {
  return awaitingUnconfirmed(entity, lessons);
}

/**
 * Lessons the member reached the end of that nobody has credited yet.
 *
 * The API drops confirmed and legacy lessons before bucketing `awaitingLessons`,
 * so on a fresh payload this filters nothing. It matters between a tap and the
 * server's reply: `setLessonMark` no longer edits `awaitingLessons` at all — the
 * member's own completion is not the leader's to rewrite — so a just-confirmed
 * lesson is briefly in both lists, and only this keeps it out of the prompt.
 */
export function awaitingUnconfirmed(entity, lessons) {
  const awaiting = listOf(entity, 'awaitingLessons');
  return ordinalsFrom(lessons).filter(
    ordinal => awaiting.includes(ordinal) && !isCredited(entity, ordinal),
  );
}

/**
 * Finished progress through each booklet as a percentage, in part order — the
 * two halves of the progress track on the detail sheet and the CG roster.
 *
 * Each half is sized from its own part: a booklet holds four lessons on the old
 * curriculum and five on the new one, and the two need not match each other.
 */
export function partPercentages(entity, lessons) {
  const credited = creditedLessons(entity);
  return partsFrom(lessons).map(group => {
    const inPart = group.lessons.filter(lesson => credited.includes(lesson.ordinal)).length;
    return group.lessons.length ? (inPart / group.lessons.length) * 100 : 0;
  });
}

/**
 * { kind, text, dot, done, faint? } for one roster row's status line — shared by
 * the card grid (StudentCard) and the read-only CG roster (CgDetail).
 *
 * `kind` is what lets one caller show a line the other hides: the roster card
 * drops `kind: 'part'` (the "Part 1 · 3 of 5" reading) because the chip grid
 * right beneath it already says the same thing in a form you can act on, while
 * the CG view has no chips and needs the words.
 */
export function progressStatus(entity, lessons) {
  const credited = creditedLessons(entity);
  const parts = partsFrom(lessons);
  const notStarted = {
    kind: 'none',
    text: 'Not started',
    dot: 'var(--ldr-muted)',
    done: false,
    faint: true,
  };

  if (isJourneyComplete(entity, (lessons || []).length)) {
    return { kind: 'complete', text: 'Journey complete', dot: 'var(--ldr-good)', done: true };
  }
  if (!credited.length) {
    // Nothing confirmed yet, but the member is in a lesson. Calling that "Not
    // started" was untrue and hid the members a leader most needs to notice.
    //
    // Two different facts, and they must not blur: a lesson the member is still
    // WRITING in is "in progress"; one they reached the end of is FINISHED and
    // waiting on the leader. Reporting the second as in progress put "In
    // progress · Lesson 1" directly above "Finished a lesson — confirm?" on the
    // same card, about the same lesson.
    const active = activeLessons(entity, lessons);
    const writing = active.find(ordinal => lessonState(entity, ordinal) === 'started');
    if (writing !== undefined) {
      return {
        kind: 'started',
        text: `In progress · Lesson ${writing}`,
        dot: 'var(--ldr-accent)',
        done: false,
      };
    }
    if (active.length) {
      // Only finished-and-waiting lessons. The card hides this kind — its green
      // prompt already says it — but the CG roster has no chips and needs words.
      return {
        kind: 'awaiting',
        text: `Finished Lesson ${active[0]} · awaiting confirmation`,
        dot: 'var(--ldr-good)',
        done: false,
      };
    }
    return notStarted;
  }

  // Report against the part the member is actually working in: the first that
  // is not yet finished, falling back to the last when everything but the
  // journey total is done.
  const counted = parts.map(group => ({
    ...group,
    done: group.lessons.filter(lesson => credited.includes(lesson.ordinal)).length,
  }));
  const current = counted.find(group => group.done < group.lessons.length) || counted[0];
  if (!current) return notStarted;

  return {
    kind: 'part',
    text: `${partLabel(current.part)} · ${current.done} of ${current.lessons.length}`,
    dot: current.part === 1 ? 'var(--ldr-fs)' : 'var(--ldr-ns)',
    done: false,
  };
}
